import type { ReactNode } from 'react';
import { IconButton, Spinner } from '../../ui/controls';

/**
 * The top of an Assets panel: what kind it is and the way out. Every kind wears it; the Brand tab wears it only
 * until there is a brand to put in its place. A panel a level down (a place in My Stuff)
 * wears the way back in the title's place, and a kind may add its own button before Close.
 */
export function PanelHeader({ title, onClose, back, backLabel, extra }: { title: string; onClose: () => void; back?: () => void; backLabel?: string; extra?: ReactNode }) {
  return (
    <header className="library-panel-header flex h-[46px] shrink-0 items-center gap-[8px] pl-[16px] pr-[8px]">
      {back && <IconButton icon="back" label={`Back to ${backLabel ?? 'My Stuff'}`} onClick={back} />}
      <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-label">{title}</h2>
      {extra}
      <IconButton icon="close" label="Close Assets" onClick={onClose} />
    </header>
  );
}

/**
 * A heading over a run of things, in a panel of the Assets library. The stand-in tiles,
 * sample strips and "See All" links that used to live here went with the mock panels
 * they were drawn for: nothing in Assets is a placeholder now.
 */
export function Section({ title, children }: { title: string; children: ReactNode; all?: boolean }) {
  return (
    <section className="mb-[18px]">
      <h3 className="mb-[8px] text-[13px] font-semibold text-label">{title}</h3>
      {children}
    </section>
  );
}

/** A list's end says whether more is coming, can be requested, or is finished. */
export function LibraryPagination({ label, hasMore, loading, problem, onMore }: {
  label: string; hasMore: boolean; loading: boolean; problem?: string; onMore(): void;
}) {
  return <div className="mt-[14px] border-t border-separator pt-[12px] text-center text-[12px] text-label-2" data-library-pagination>
    {loading ? <div role="status" className="flex min-h-[32px] items-center justify-center gap-[8px]">
      <Spinner size={14} /><span>Loading more {label}…</span>
    </div> : problem ? <>
      <p role="status" className="mb-[8px]">{problem}</p>
      <button type="button" onClick={onMore} className="w-full rounded-[8px] bg-fill-2 px-[12px] py-[8px] font-medium text-label hover:bg-fill-1">Try again</button>
    </> : hasMore ? <button type="button" onClick={onMore} className="w-full rounded-[8px] bg-fill-2 px-[12px] py-[8px] font-medium text-label hover:bg-fill-1">Load more {label}</button>
    : <p role="status" className="py-[8px]">All {label} loaded</p>}
  </div>;
}
